import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

const lotteriesData = {
  'Europe': [
    { name: 'Swiss Lotto 🇨🇭', code: 'swisslotto', status: 'active' },
    { name: 'EuroMillions 🇪🇺', code: 'euromillions', status: 'active' },
    { name: 'EuroDreams 🇪🇺', code: 'eurodreams', status: 'active' },
    { name: 'SuperEnalotto 🇮🇹', code: 'superenalotto', status: 'active' },
    { name: 'La Primitiva 🇪🇸', code: 'laprimitiva', status: 'active' },
    { name: 'German Lotto 6aus49 🇩🇪', code: 'german-lotto', status: 'active' },
    { name: 'UK Lotto 🇬🇧', code: 'uk-lotto', status: 'active' },
    { name: 'Irish Lotto 🇮🇪', code: 'irish-lotto', status: 'active' },
    { name: 'EuroJackpot 🇪🇺', code: 'eurojackpot', status: 'active' },
    { name: 'BonoLoto 🇪🇸', code: 'bonoloto', status: 'pending' },
    { name: 'Viking Lotto 🇳🇴', code: 'viking-lotto', status: 'pending' }
  ],
  'Americas': [
    { name: 'Mega Millions 🇺🇸', code: 'megamillions', status: 'active' },
    { name: 'Powerball 🇺🇸', code: 'powerball', status: 'active' },
    { name: 'SuperLotto Plus 🇺🇸', code: 'superlottoplus', status: 'pending' },
    { name: 'Lotto Max 🇨🇦', code: 'lotto-max', status: 'pending' },
    { name: 'Mega-Sena 🇧🇷', code: 'megasena', status: 'active' }
  ],
  'Australia': [
    { name: 'Australian Lotto 🇦🇺', code: 'australian-lotto', status: 'active' },
    { name: 'Powerball AU 🇦🇺', code: 'australia-powerball', status: 'pending' },
    { name: 'Oz Lotto 🇦🇺', code: 'oz-lotto', status: 'pending' }
  ]
};

const regionTranslations = {
  'Europe': 'home.regions.europe',
  'Americas': 'home.regions.americas',
  'Australia': 'home.regions.australia'
};

const LotteriesPage = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { t, currentLang } = useLanguage();

  // Determine the correct base path for links
  // If we're at root path (no /en prefix) and current language is English, use root paths
  const isAtRoot = !location.pathname.startsWith('/en') && !location.pathname.startsWith('/fr') && !location.pathname.startsWith('/it');
  const basePath = (isAtRoot && currentLang === 'en') ? '' : `/${currentLang}`;

  return (
    <div className="min-h-screen py-6">
      <SEOHead title={t('lotteries.title')} description={t('lotteries.title')} path="/lotteries" />
      
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-white mb-8">{t('lotteries.title')}</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Object.entries(lotteriesData).map(([region, lotteries]) => (
            <div key={region}>
              <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wide mb-3">
                {t(regionTranslations[region], region)}
              </h2>
              <div className="space-y-2">
                {lotteries.map((lottery) => {
                  const isActive = lottery.status === 'active';

                  return (
                    <div
                      key={lottery.code}
                      className={`block px-3 py-2 rounded-lg text-sm transition-all ${
                        isActive
                          ? 'bg-gray-900/40 border border-gray-700/50 hover:border-amber-500/50 text-white'
                          : 'bg-gray-900/20 border border-gray-800/30 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {isActive ? (
                          <Link
                            to={`${basePath}/lottery/${lottery.code}`}
                            className="font-medium flex-1 hover:text-amber-400 transition-colors"
                          >
                            {lottery.name}
                          </Link>
                        ) : (
                          <span>{lottery.name}</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          isActive 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-700/20 text-gray-600'
                        }`}>
                          {isActive ? t('lotteries.active') : t('lotteries.pending')}
                        </span>
                      </div>
                      {/* Subpage links for active lotteries - SEO crawl coverage */}
                      {isActive && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          <Link to={`${basePath}/lottery/${lottery.code}/results`} className="text-[10px] text-gray-500 hover:text-amber-400">Results</Link>
                          <span className="text-gray-700">•</span>
                          <Link to={`${basePath}/lottery/${lottery.code}/statistics`} className="text-[10px] text-gray-500 hover:text-amber-400">Stats</Link>
                          <span className="text-gray-700">•</span>
                          <Link to={`${basePath}/lottery/${lottery.code}/predictions`} className="text-[10px] text-gray-500 hover:text-amber-400">Predictions</Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        
        {/* BACK TO HOME - Bidirectional linking */}
        <div className="mt-8 text-center">
          <Link 
            to={`/${lang}`}
            className="text-sm text-gray-400 hover:text-amber-400 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LotteriesPage;
